/**
 * Que formas de entrega ofrece hoy la tienda, para las pantallas que la
 * anuncian: la barra de la cabecera, la portada, la ficha, el carro y el
 * checkout.
 *
 * La regla NO se decide aca: la resuelve el backend a partir de lo que el panel
 * dejo encendido y la manda lista en `OpcionesDespacho.entregas`. Estas
 * funciones solo la leen, para que ninguna pantalla prometa "despacho gratis"
 * el dia en que el despacho esta apagado.
 *
 * Mientras las opciones no llegan (`null`) todas responden que no: es mejor no
 * anunciar un despacho por un instante que anunciarlo cuando no existe.
 */
import type { OpcionesDespacho, TipoEntrega } from '@elcarpintero/compartido';

export function ofreceDespacho(opciones: OpcionesDespacho | null): boolean {
  return opciones !== null && opciones.entregas.includes('despacho');
}

export function ofreceRetiro(opciones: OpcionesDespacho | null): boolean {
  return opciones !== null && opciones.entregas.includes('retiro');
}

/**
 * Desde cuanto el despacho es gratis, o `null` si hoy no hay despacho gratis:
 * el panel apago el despacho o solo el gratis (se cobra siempre), o las
 * opciones no llegan todavia.
 */
export function umbralDespachoGratis(opciones: OpcionesDespacho | null): number | null {
  if (opciones === null || !ofreceDespacho(opciones)) return null;
  return opciones.despachoGratisDesde;
}

/** El subtotal ya no paga despacho. Mismo umbral que `calcularCostoDespacho()` del backend. */
export function alcanzaDespachoGratis(opciones: OpcionesDespacho | null, subtotal: number): boolean {
  const umbral = umbralDespachoGratis(opciones);
  return umbral !== null && subtotal >= umbral;
}

/**
 * Ni despacho ni retiro: el sitio no cobra y la compra se cierra por WhatsApp,
 * donde se acuerdan la entrega y su costo.
 */
export function soloWhatsapp(opciones: OpcionesDespacho | null): boolean {
  return opciones !== null && opciones.entregas.length === 0;
}

/**
 * La entrega que vale para el formulario: la elegida si se sigue ofreciendo, y
 * si no, la primera que se ofrece. Cubre al comprador que vuelve con un checkout
 * guardado cuando el panel ya apago lo que habia elegido.
 */
export function entregaVigente(elegida: TipoEntrega, opciones: OpcionesDespacho | null): TipoEntrega {
  if (opciones === null || opciones.entregas.includes(elegida)) return elegida;
  return opciones.entregas[0] ?? elegida;
}
